import { ConfigSaveErrorCode, saveErrorCode } from '@/domain/configuracao/helpers/saveError';
import {
  IConfigInstitucional,
  ICreateFeriadoPayload,
  IUpdateConfigPayload,
  IUpdateFeriadoPayload,
} from '@/domain/configuracao/interfaces/configuracao';
import { configuracaoRepository } from '@/domain/configuracao/repositories/configuracaoRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

interface ConfiguracaoState {
  config: IConfigInstitucional | null;
  loading: boolean;
  failed: boolean;
  saving: boolean;
  /** Why the last mutation failed, for the screen to say it in pt and en. */
  lastErrorCode: ConfigSaveErrorCode | null;
  load: () => Promise<void>;
  update: (payload: IUpdateConfigPayload) => Promise<boolean>;
  addFeriado: (payload: ICreateFeriadoPayload) => Promise<boolean>;
  updateFeriado: (id: string, payload: IUpdateFeriadoPayload) => Promise<boolean>;
  removeFeriado: (id: string) => Promise<boolean>;
  /**
   * The two writes of the trash THROW instead of answering `false`: the API refuses a
   * permanent delete with a sentence that names the days still to go,
   * and that sentence is what the screen has to show, not a generic save error.
   */
  restaurarFeriado: (id: string) => Promise<void>;
  removeFeriadoDefinitivo: (id: string) => Promise<void>;
  /** Takes the roadmap alert off a holiday, or puts it back. No date is recalculated. */
  setFeriadoConfirmado: (id: string, confirmado: boolean) => Promise<boolean>;
  /** Marks a parameter label as validated with the client, or puts the previous label back. */
  setOrigemConfirmada: (campo: string, confirmado: boolean) => Promise<boolean>;
}

export const configuracaoStore = createStore<ConfiguracaoState>((set) => ({
  config: null,
  loading: false,
  failed: false,
  saving: false,
  lastErrorCode: null,

  load: async () => {
    set({ loading: true });
    try {
      const config = await configuracaoRepository.get();
      set({ config, failed: false });
    } catch (error) {
      logger.error('[configuracaoStore] failed to load config', error);
      set({ failed: true });
    } finally {
      set({ loading: false });
    }
  },

  update: async (payload) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.update(payload);
      set({ config });
      return true;
    } catch (error) {
      logger.error('[configuracaoStore] failed to update config', error);
      set({ lastErrorCode: saveErrorCode(error) });
      return false;
    } finally {
      set({ saving: false });
    }
  },

  addFeriado: async (payload) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.addFeriado(payload);
      set({ config });
      return true;
    } catch (error) {
      logger.error('[configuracaoStore] failed to add feriado', error);
      set({ lastErrorCode: saveErrorCode(error) });
      return false;
    } finally {
      set({ saving: false });
    }
  },

  updateFeriado: async (id, payload) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.updateFeriado(id, payload);
      set({ config });
      return true;
    } catch (error) {
      logger.error('[configuracaoStore] failed to update feriado', error);
      set({ lastErrorCode: saveErrorCode(error) });
      return false;
    } finally {
      set({ saving: false });
    }
  },

  removeFeriado: async (id) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.removeFeriado(id);
      set({ config });
      return true;
    } catch (error) {
      logger.error('[configuracaoStore] failed to remove feriado', error);
      set({ lastErrorCode: saveErrorCode(error) });
      return false;
    } finally {
      set({ saving: false });
    }
  },

  restaurarFeriado: async (id) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.restaurarFeriado(id);
      set({ config });
    } finally {
      set({ saving: false });
    }
  },

  removeFeriadoDefinitivo: async (id) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.removeFeriadoDefinitivo(id);
      set({ config });
    } finally {
      set({ saving: false });
    }
  },

  setFeriadoConfirmado: async (id, confirmado) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.setFeriadoConfirmado(id, confirmado);
      set({ config });
      return true;
    } catch (error) {
      logger.error('[configuracaoStore] failed to confirm feriado', error);
      set({ lastErrorCode: saveErrorCode(error) });
      return false;
    } finally {
      set({ saving: false });
    }
  },

  setOrigemConfirmada: async (campo, confirmado) => {
    set({ saving: true });
    try {
      const config = await configuracaoRepository.setOrigemConfirmada(campo, confirmado);
      set({ config });
      return true;
    } catch (error) {
      logger.error('[configuracaoStore] failed to confirm origem', error);
      set({ lastErrorCode: saveErrorCode(error) });
      return false;
    } finally {
      set({ saving: false });
    }
  },
}));
